import { useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import { sendTransactionalEmail } from '@/lib/emailService';
import { EMAIL_TEMPLATES, EmailType } from '@/lib/mailTemplates';
import { Loader2, Mail } from 'lucide-react';

const EMAIL_TYPES: { value: EmailType; label: string }[] = [
  { value: 'welcome', label: 'Welcome Email' },
  { value: 'password_reset', label: 'Password Reset' },
  { value: 'password_changed', label: 'Password Changed' },
  { value: 'billing_receipt', label: 'Billing Receipt' },
  { value: 'payment_failed', label: 'Payment Failed' },
  { value: 'credit_reminder', label: 'Credit Reminder' },
  { value: 'subscription_renewal', label: 'Subscription Renewal' },
  { value: 'plan_upgrade', label: 'Plan Upgrade' },
  { value: 'team_invite', label: 'Team Invite' },
  { value: 'invoice_download', label: 'Invoice Download' },
  { value: 'security_notification', label: 'Security Notification' },
  { value: 'reactivation', label: 'Reactivation' },
  { value: 'feedback_request', label: 'Feedback Request' },
  { value: 'gdpr_deletion', label: 'GDPR Deletion' },
];

export function EmailTester() {
  const [emailType, setEmailType] = useState<EmailType>('welcome');
  const [recipientEmail, setRecipientEmail] = useState('weissachturbo@gmail.com');
  const [recipientName, setRecipientName] = useState('John Doe');
  const [templateVars, setTemplateVars] = useState<Record<string, any>>({});
  const [sending, setSending] = useState(false);

  const getTemplateVarsForType = (type: EmailType): { key: string; label: string; defaultValue: string }[] => {
    switch (type) {
      case 'password_reset':
        return [{ key: 'resetLink', label: 'Reset Link', defaultValue: 'https://email2contact.com/reset?token=test123' }];
      case 'password_changed':
        return [{ key: 'date', label: 'Date', defaultValue: new Date().toLocaleDateString('de-DE') }];
      case 'billing_receipt':
        return [
          { key: 'planName', label: 'Plan Name', defaultValue: 'Pro' },
          { key: 'amount', label: 'Amount (€)', defaultValue: '29.99' },
          { key: 'credits', label: 'Credits', defaultValue: '500' },
          { key: 'invoiceUrl', label: 'Invoice URL', defaultValue: 'https://email2contact.com/invoices/123' },
        ];
      case 'credit_reminder':
        return [{ key: 'usedPercent', label: 'Used Percent', defaultValue: '85' }];
      case 'subscription_renewal':
        return [
          { key: 'planName', label: 'Plan Name', defaultValue: 'Pro' },
          { key: 'renewalDate', label: 'Renewal Date', defaultValue: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toLocaleDateString('de-DE') },
        ];
      case 'plan_upgrade':
        return [
          { key: 'oldPlan', label: 'Old Plan', defaultValue: 'Starter' },
          { key: 'newPlan', label: 'New Plan', defaultValue: 'Pro' },
          { key: 'newCredits', label: 'New Credits', defaultValue: '500' },
        ];
      case 'team_invite':
        return [
          { key: 'inviter', label: 'Inviter Name', defaultValue: 'John Doe' },
          { key: 'inviteUrl', label: 'Invite URL', defaultValue: 'https://email2contact.com/invite/abc123' },
        ];
      case 'invoice_download':
        return [{ key: 'invoiceUrl', label: 'Invoice URL', defaultValue: 'https://email2contact.com/invoices/456.pdf' }];
      case 'security_notification':
        return [
          { key: 'deviceInfo', label: 'Device Info', defaultValue: 'Chrome on Windows' },
          { key: 'date', label: 'Date', defaultValue: new Date().toLocaleDateString('de-DE') },
        ];
      default:
        return [];
    }
  };

  const handleEmailTypeChange = (value: EmailType) => {
    setEmailType(value);
    const fields = getTemplateVarsForType(value);
    const newVars: Record<string, any> = {};
    fields.forEach(field => {
      newVars[field.key] = field.defaultValue;
    });
    setTemplateVars(newVars);
  };

  const handleSendTest = async () => {
    if (!recipientEmail || !recipientName) {
      toast.error('Bitte E-Mail und Name eingeben');
      return;
    }

    setSending(true);
    try {
      const varsToSend: Record<string, any> = {};
      Object.entries(templateVars).forEach(([key, value]) => {
        varsToSend[key] = key === 'amount' || key === 'credits' || key === 'newCredits' || key === 'usedPercent' 
          ? parseFloat(value) 
          : value;
      });

      const result = await sendTransactionalEmail(
        emailType,
        recipientEmail,
        recipientName,
        varsToSend
      );

      if (result.success) {
        toast.success('Test-E-Mail erfolgreich versendet!');
      } else {
        toast.error('Fehler beim Versenden: ' + (result.error as any)?.message);
      }
    } catch (error: any) {
      toast.error('Fehler: ' + error.message);
    } finally {
      setSending(false);
    }
  };

  const currentFields = getTemplateVarsForType(emailType);
  const template = EMAIL_TEMPLATES[emailType](templateVars);

  return (
    <div className="space-y-6">
      <Card className="glass-card-strong hover-lift transition-all">
        <CardHeader>
          <div className="flex items-center gap-2">
            <div className="w-10 h-10 rounded-full bg-gradient-to-r from-blue-500/10 to-purple-500/10 flex items-center justify-center animate-float">
              <Mail className="h-5 w-5 text-primary" />
            </div>
            <div>
              <CardTitle className="text-gradient">E-Mail Tester</CardTitle>
              <CardDescription>Teste alle Transaktions-E-Mails</CardDescription>
            </div>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="emailType">E-Mail Typ</Label>
            <Select value={emailType} onValueChange={handleEmailTypeChange}>
              <SelectTrigger id="emailType">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {EMAIL_TYPES.map(type => (
                  <SelectItem key={type.value} value={type.value}>
                    {type.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="recipientEmail">Empfänger E-Mail</Label>
              <Input
                id="recipientEmail"
                type="email"
                value={recipientEmail}
                onChange={(e) => setRecipientEmail(e.target.value)}
                placeholder="test@example.com"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="recipientName">Empfänger Name</Label>
              <Input
                id="recipientName"
                value={recipientName}
                onChange={(e) => setRecipientName(e.target.value)}
                placeholder="Max Mustermann"
              />
            </div>
          </div>

          {currentFields.length > 0 && (
            <div className="space-y-2">
              <Label className="text-base">Template-Variablen</Label>
              <div className="grid grid-cols-2 gap-4">
                {currentFields.map(field => (
                  <div key={field.key} className="space-y-2">
                    <Label htmlFor={field.key}>{field.label}</Label>
                    <Input
                      id={field.key}
                      value={templateVars[field.key] || ''}
                      onChange={(e) => setTemplateVars({ ...templateVars, [field.key]: e.target.value })}
                    />
                  </div>
                ))}
              </div>
            </div>
          )}

          <Button onClick={handleSendTest} disabled={sending} className="w-full">
            {sending ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Wird gesendet...
              </>
            ) : (
              'Test-E-Mail senden'
            )}
          </Button>
        </CardContent>
      </Card>

      <Card className="glass-card-strong hover-lift transition-all">
        <CardHeader>
          <CardTitle className="text-gradient">Template Vorschau</CardTitle>
        </CardHeader>
        <CardContent className="space-y-2">
          <div>
            <Label className="text-muted-foreground">Betreff:</Label>
            <p className="font-medium">{template.subject}</p>
          </div>
          <div>
            <Label className="text-muted-foreground">Header:</Label>
            <p className="font-medium">{template.header}</p>
          </div>
          <div>
            <Label className="text-muted-foreground">Nachricht:</Label>
            <p>{template.message}</p>
          </div>
          {template.ctaText && (
            <div>
              <Label className="text-muted-foreground">CTA Button:</Label>
              <p className="font-medium">{template.ctaText} → {template.ctaUrl}</p>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
